import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import environment from '../config/environment';
import { getApiUrl } from '../config/api';
import { tieneRondaLocal } from '../utils/rondaOfflineStorage';

const COMPANY_NAME = environment.companyName || 'Moreseg Cia. Ltda.';

const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const userRol = user?.rol || '';
  const [opcionesPersonalizadas, setOpcionesPersonalizadas] = useState([]);
  const [mostrarRonda, setMostrarRonda] = useState(false);

  useEffect(() => {
    if (userRol !== 'V' || !user?.id_generado) {
      setOpcionesPersonalizadas([]);
      return;
    }
    const url = new URL(getApiUrl('BITACORA', 'OPCIONES_PERSONALIZADAS'));
    url.searchParams.set('idGenerado', String(user.id_generado));
    fetch(url.toString(), { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (
          data.success &&
          data.data &&
          data.data.mostrarOpcionesPersonalizadas === true &&
          Array.isArray(data.data.opciones)
        ) {
          setOpcionesPersonalizadas(data.data.opciones);
        } else {
          setOpcionesPersonalizadas([]);
        }
      })
      .catch(() => setOpcionesPersonalizadas([]));
  }, [userRol, user?.id_generado]);

  useEffect(() => {
    if (userRol !== 'V' || !user?.id_generado || !user?.username) {
      setMostrarRonda(false);
      return;
    }
    const local = tieneRondaLocal(user.username, user.id_generado);
    const url = new URL(getApiUrl('RONDA_OFFLINE', 'ESTADO'));
    url.searchParams.set('idGenerado', String(user.id_generado));
    url.searchParams.set('username', String(user.username));
    fetch(url.toString(), { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data && data.data.mostrar === true) {
          setMostrarRonda(true);
        } else {
          setMostrarRonda(local);
        }
      })
      .catch(() => setMostrarRonda(local));
  }, [userRol, user?.id_generado, user?.username]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Cerrar sidebar en móvil después de navegar
    if (onClose) {
      onClose();
    }
  };

  const handleOpcionPersonalizada = (opcion) => {
    navigate('/bitacora-personalizada', { state: { codigo: opcion.codigo, nombre: opcion.nombre } });
    if (onClose) onClose();
  };

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-56 sm:w-64 bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-2 sm:px-4 py-4 border-b border-gray-200">
            <div className="flex-1 flex flex-col items-center text-center space-y-2">
              <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                <img
                  src={environment.logoPath}
                  alt={`Logo ${COMPANY_NAME}`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h1 className="text-sm sm:text-base md:text-xl font-bold text-gray-800 leading-tight">{COMPANY_NAME}</h1>
              <p className="text-xs text-gray-600">Bitácora Electrónica</p>
            </div>
            {/* Botón de cerrar - Solo visible en móvil */}
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
              aria-label="Cerrar menú"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="mt-4 sm:mt-6 px-2 sm:px-4 flex-1 overflow-y-auto">
            <div className="pb-6 space-y-6">
              {/* Secciones visibles solo para Vigilantes (rol 'V') */}
              {userRol === 'V' && (
                <>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Inicio</p>
                    <div className="space-y-1">
                      {/* Dashboard Vigilancia - Solo visible para Vigilantes (rol 'V') */}
                      <button
                        onClick={() => handleNavigation('/dashboard')}
                        className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duración-200 ${
                          isActive('/dashboard')
                            ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
                        </svg>
                        <span className="truncate">Dashboard Vigilancia</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Relevar</p>
              <div className="space-y-1">
                <button
                  onClick={() => handleNavigation('/relevos')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duración-200 ${
                    isActive('/relevos')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 00-2-2h-4m6 2a2 2 0 01-2 2h-4m-8 0a2 2 0 002 2h4m-6-2a2 2 0 012-2h4m0 0V7m0 4v4" />
                  </svg>
                  <span className="truncate">Relevos</span>
                </button>
              </div>
            </div>

            {mostrarRonda && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Ronda</p>
                <div className="space-y-1">
                  <button
                    onClick={() => handleNavigation('/ronda')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/ronda')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <span className="truncate">Ronda</span>
                  </button>
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Eventos</p>
              <div className="space-y-1">
                <button
                  onClick={() => handleNavigation('/novedades/registrar')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duración-200 ${
                    isActive('/novedades/registrar')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v8m-4-4h8m7 0A9 9 0 1112 3a9 9 0 0111 9z" />
                  </svg>
                  <span className="truncate">Registrar evento</span>
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Vehículo Institucional</p>
              <div className="space-y-1">
                <button
                  onClick={() => handleNavigation('/vehiculos-institucionales/salida')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duración-200 ${
                    isActive('/vehiculos-institucionales/salida')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="truncate">Salida Vehículo</span>
                </button>
                <button
                  onClick={() => handleNavigation('/vehiculos-institucionales/retorno')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duración-200 ${
                    isActive('/vehiculos-institucionales/retorno')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18m-6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="truncate">Retorno Vehículo</span>
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Personas</p>
              <div className="space-y-1">
                <button
                  onClick={() => handleNavigation('/visitantes/registrar')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transición-colors duración-200 ${
                    isActive('/visitantes/registrar')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 7l9-5-9-5-9 5 9 5z" />
                  </svg>
                  <span className="truncate">Registrar Ingreso</span>
                </button>
                <button
                  onClick={() => handleNavigation('/visitantes/salida')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transición-colors duración-200 ${
                    isActive('/visitantes/salida')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="truncate">Registrar Salida</span>
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Vehículos</p>
              <div className="space-y-1">
                <button
                  onClick={() => handleNavigation('/vehiculos/registrar')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transición-colors duración-200 ${
                    isActive('/vehiculos/registrar')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13l2-2m0 0l3 3 7-7 4 4 2-2m-6 11H5a2 2 0 01-2-2V7a2 2 0 012-2h3m4 14h6a2 2 0 002-2V9a2 2 0 00-2-2h-3" />
                  </svg>
                  <span className="truncate">Registrar vehículo</span>
                </button>
                <button
                  onClick={() => handleNavigation('/vehiculos/salida')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transición-colors duración-200 ${
                    isActive('/vehiculos/salida')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="truncate">Salida de vehículo</span>
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Consultas</p>
              <div className="space-y-1">
                <button
                  onClick={() => handleNavigation('/consultas/eventos')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                    isActive('/consultas/eventos')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="truncate">Consultar eventos</span>
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Utilidades</p>
              <div className="space-y-1">
                <button
                  onClick={() => handleNavigation('/utilidades/gestionar-catalogo')}
                  className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                    isActive('/utilidades/gestionar-catalogo')
                      ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  <span className="truncate">Gestionar Catalogo</span>
                </button>
              </div>
            </div>

            {/* Bitácora personalizada: opciones del catálogo cuando bitacora_personalizada es 1 o 2 (API) */}
            {opcionesPersonalizadas.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Bitácora personalizada</p>
                <div className="space-y-1">
                  {opcionesPersonalizadas.map((opcion) => (
                    <button
                      key={opcion.id}
                      onClick={() => handleOpcionPersonalizada(opcion)}
                      className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                        location.pathname === '/bitacora-personalizada' && location.state?.codigo === opcion.codigo
                          ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-2a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      <span className="truncate">{opcion.nombre}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
                </>
              )}

            {/* Supervisión - Solo visible para Supervisores (rol 'S') */}
            {userRol === 'S' && (
              <>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Inicio</p>
                  <div className="space-y-1">
                    {/* Dashboard Supervisión - Solo visible para Supervisores (rol 'S') */}
                    <button
                      onClick={() => handleNavigation('/dashboard-supervision')}
                      className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                        isActive('/dashboard-supervision')
                          ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
                      </svg>
                      <span className="truncate">Dashboard Supervisión</span>
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Supervisión</p>
                  <div className="space-y-1">
                    <button
                      onClick={() => handleNavigation('/supervision/registrar-asistencia')}
                      className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                        isActive('/supervision/registrar-asistencia')
                          ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="truncate">Registrar Asistencia</span>
                    </button>
                  <button
                    onClick={() => handleNavigation('/supervision/visitas-realizadas')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/supervision/visitas-realizadas')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    <span className="truncate">Visitas Realizadas</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/supervision/control-relevos')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/supervision/control-relevos')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="truncate">Control de Relevos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/supervision/armamento')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/supervision/armamento')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h10M4 17h6" />
                    </svg>
                    <span className="truncate">Control de Armamento</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/supervision/sanciones')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/supervision/sanciones')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="truncate">Sanciones</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/supervision/certificado-multa-falta-punto')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/supervision/certificado-multa-falta-punto')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="truncate">Certificados médicos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/supervision/mis-rutas')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/supervision/mis-rutas')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <span className="truncate">Mis rutas</span>
                  </button>
                  {!environment.isProduction && (
                  <button
                    onClick={() => handleNavigation('/supervision/reenvio-alerta')}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive('/supervision/reenvio-alerta')
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span className="truncate">Reenvío de notificación</span>
                  </button>
                  )}
                </div>
              </div>
              </>
            )}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-2 sm:p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors duración-200"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="truncate">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar; 